/**
 * Monthly Drill-Down Page
 * Detailed monthly analysis with client profitability and service breakdown
 */

import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Calendar, DollarSign, Users, Package, Clock,
  TrendingUp, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle
} from 'lucide-react';
import { useAnalytics } from './AnalyticsContext';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const MonthlyDrillDown = () => {
  const {
    monthlyData,
    monthlyLoading,
    monthlyError,
    fetchMonthlyData,
    selectedMonth,
    formatCurrency
  } = useAnalytics();

  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  useEffect(() => {
    fetchMonthlyData(currentDate.year, currentDate.month);
  }, [currentDate, fetchMonthlyData]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;

      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      } else if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }

      return { year: newYear, month: newMonth };
    });
  };

  const formatMonthYear = (year, month) => {
    return new Date(year, month - 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  if (monthlyLoading && !monthlyData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading monthly data...</p>
        </div>
      </div>
    );
  }

  if (monthlyError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">{monthlyError}</p>
        <button
          onClick={() => fetchMonthlyData(currentDate.year, currentDate.month)}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  const kpis = monthlyData?.kpis;
  const clientProfitability = monthlyData?.clientProfitability || [];
  const serviceBreakdown = monthlyData?.serviceBreakdown || [];

  // Prepare chart data
  const clientChartData = clientProfitability
    .slice(0, 10)
    .map(client => ({
      name: client.clientName?.substring(0, 20) || 'Unknown',
      revenue: client.totalRevenue,
      units: client.totalUnits
    }));

  const serviceChartData = serviceBreakdown.map((service, index) => ({
    name: service.name || 'Other',
    value: service.revenue,
    count: service.count,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Month Navigator */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              {formatMonthYear(currentDate.year, currentDate.month)}
            </h2>
          </div>

          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={
              currentDate.year >= new Date().getFullYear() &&
              currentDate.month >= new Date().getMonth() + 1
            }
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {kpis && (
        <>
          {/* Monthly KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(kpis.revenue.netService)}
              </p>
              <p className="text-sm text-gray-500">Net Service Revenue</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(kpis.labor.totalCost)}
              </p>
              <p className="text-sm text-gray-500">Labor Cost</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className={`text-2xl font-bold ${kpis.profit.trueMonthly >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(kpis.profit.trueMonthly)}
              </p>
              <p className="text-sm text-gray-500">True Monthly Profit</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {kpis.operations.totalUnits.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Units Processed</p>
            </div>
          </div>

          {/* Additional Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {kpis.operations.shipmentCount}
              </p>
              <p className="text-xs text-gray-500">Shipments</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {kpis.operations.clientCount}
              </p>
              <p className="text-xs text-gray-500">Active Clients</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {kpis.labor.totalHours.toFixed(1)}h
              </p>
              <p className="text-xs text-gray-500">Total Hours</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {kpis.efficiency.hoursPer1000Units.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">Hrs/1k Units</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-lg font-semibold text-gray-900">
                {kpis.dataConfidenceScore}%
              </p>
              <p className="text-xs text-gray-500">Data Confidence</p>
            </div>
          </div>
        </>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Profitability Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Client Revenue (Top 10)
          </h3>
          {clientChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={clientChartData}
                layout="vertical"
                margin={{ left: 20, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'revenue' ? `$${value.toLocaleString()}` : value.toLocaleString(),
                    name === 'revenue' ? 'Revenue' : 'Units'
                  ]}
                  contentStyle={{
                    backgroundColor: '#FFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No client data available for this month
            </div>
          )}
        </div>

        {/* Service Breakdown Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Service Revenue Breakdown
          </h3>
          {serviceChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={serviceChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={{ stroke: '#6B7280', strokeWidth: 1 }}
                >
                  {serviceChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                  contentStyle={{
                    backgroundColor: '#FFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => (
                    <span className="text-sm text-gray-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No service data available for this month
            </div>
          )}
        </div>
      </div>

      {/* Revenue Breakdown Summary */}
      {kpis && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Revenue Categories
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(kpis.revenue.total)}
              </p>
            </div>
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600">Net Service Revenue</p>
              <p className="text-xl font-bold text-green-700">
                {formatCurrency(kpis.revenue.netService)}
              </p>
            </div>
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600">Pass-Through</p>
              <p className="text-xl font-bold text-blue-700">
                {formatCurrency(kpis.revenue.passThrough)}
              </p>
            </div>
            <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
              <p className="text-sm text-yellow-600">Unmapped</p>
              <p className="text-xl font-bold text-yellow-700">
                {formatCurrency(kpis.revenue.unmapped)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pass-Through Note */}
      {kpis && kpis.revenue.passThrough > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Pass-Through Revenue Note</p>
            <p className="text-sm text-blue-600 mt-1">
              {formatCurrency(kpis.revenue.passThrough)} in pass-through items (Duty, Shipping, Freight)
              is excluded from Net Service Revenue calculations per the Duty Filter rule.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyDrillDown;
